import React, { ReactElement, useState } from 'react';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';

import s from './Embla.module.scss';
import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from './EmblaCarouselArrowButtons';
import {
  determineBeforeOrAfter,
  useSelectedSnap,
} from './EmblaCarouselSelectedSnap';
import EmblaSlide from './EmblaSlide';

type SlideData = {
  key: string;
  url: string;
  title: string;
  imageName: string;
};

type PropType = {
  maxRenderedSlides: number;
  slidesData: SlideData[];
  options?: EmblaOptionsType;
};

export const emblaCarouselLastState = {
  refSelectedSnap: 0,
  refTotalSnap: 0,
};

function EmblaCarousel(props: PropType): ReactElement {
  const { maxRenderedSlides, slidesData, options } = props;

  const [refSelectedSnap] = useState(emblaCarouselLastState.refSelectedSnap);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    ...options,
    startIndex: refSelectedSnap,
  });

  const { snapInitialized, selectedSnap, totalSnap } = useSelectedSnap(
    maxRenderedSlides,
    slidesData.length,
    emblaApi,
  );
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  return (
    <div className={s.carouselContainer}>
      <div className={s.carouselViewport} ref={emblaRef}>
        <div className={s.slidesContainer}>
          {snapInitialized &&
            Array(maxRenderedSlides)
              .fill(0)
              .map((_, index) => {
                const snapOffset = determineBeforeOrAfter(
                  selectedSnap,
                  maxRenderedSlides,
                  index,
                );

                let contentClass = s.content;
                if (snapOffset < 0) {
                  contentClass = `${contentClass} ${s.before}`;
                } else if (snapOffset > 0) {
                  contentClass = `${contentClass} ${s.after}`;
                } else {
                  contentClass = `${contentClass} ${s.center}`;
                }

                let slideDataIndex = totalSnap + snapOffset;
                while (slideDataIndex < 0) {
                  slideDataIndex += slidesData.length;
                }
                slideDataIndex %= slidesData.length;

                const slideData = slidesData[slideDataIndex];

                return (
                  <EmblaSlide
                    key={`slide_${index}`} // eslint-disable-line react/no-array-index-key
                    url={slideData.url}
                    title={slideData.title}
                    imageName={slideData.imageName}
                    contentClass={contentClass}
                    isCenter={index === selectedSnap}
                  />
                );
              })}
        </div>
        <div className={s.fadeLeft} />
        <div className={s.fadeRight} />
      </div>
      <div className={s.carouselButtons}>
        <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
        <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
      </div>
    </div>
  );
}

EmblaCarousel.defaultProps = {
  options: undefined,
};

export default EmblaCarousel;
